'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// CardRail — a horizontal scrolling shelf ("rail") of cards with a header
// row and prev/next paging chevrons. App-agnostic: children are arbitrary
// cards and consumers control card min-widths. Native wheel / touch /
// keyboard scrolling is untouched — the chevrons only add scrollBy paging.
import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '../Button/Button';
import { Row } from '../server/layout/Row/index';
import { Stack } from '../server/layout/Stack/index';
import { I } from '../system/icons';
import styles from './CardRail.module.css';
// The curated I map has no right-facing chevron (I.back is ChevronLeft;
// I.arrow is a full arrow that would mismatch the chevron pair), so the
// next glyph is a feature-local lucide import per the curated-map rule.
// Size/stroke match system/icons.tsx so the pair renders identically.
const chevronRight = _jsx(ChevronRight, { size: "100%", strokeWidth: 1.75 });
const EDGE_EPSILON_PX = 2;
export function CardRail({ title, subtitle, actions, actionsVisibility = 'always', gap = 'lg', ariaLabel, children, }) {
    const regionRef = useRef(null);
    const [edges, setEdges] = useState({
        overflowing: false,
        atStart: true,
        atEnd: true,
    });
    useLayoutEffect(() => {
        const region = regionRef.current;
        if (!region)
            return;
        const remeasure = () => {
            const { scrollLeft, scrollWidth, clientWidth } = region;
            const next = {
                overflowing: scrollWidth - clientWidth > EDGE_EPSILON_PX,
                atStart: scrollLeft <= EDGE_EPSILON_PX,
                atEnd: scrollLeft + clientWidth >= scrollWidth - EDGE_EPSILON_PX,
            };
            setEdges((prev) => prev.overflowing === next.overflowing &&
                prev.atStart === next.atStart &&
                prev.atEnd === next.atEnd
                ? prev
                : next);
        };
        remeasure();
        const ro = new ResizeObserver(remeasure);
        ro.observe(region);
        region.addEventListener('scroll', remeasure, { passive: true });
        return () => {
            ro.disconnect();
            region.removeEventListener('scroll', remeasure);
        };
    }, []);
    const page = (direction) => {
        const region = regionRef.current;
        if (!region)
            return;
        region.scrollBy({ left: direction * region.clientWidth * 0.9, behavior: 'smooth' });
    };
    return (_jsxs(Stack, { gap: "lg", children: [_jsxs(Row, { justify: "space-between", align: "center", gap: "md", wrap: false, children: [_jsxs(Stack, { gap: "xs", style: { minWidth: 0, flex: '1 1 auto' }, children: [title, subtitle != null && (_jsx("div", { style: { color: 'var(--muted-foreground)' }, children: subtitle }))] }), (actions != null || edges.overflowing) && (_jsxs(Row, { gap: "sm", align: "center", style: { flexShrink: 0 }, children: [actionsVisibility === 'always' || edges.overflowing
                                ? actions
                                : null, edges.overflowing && (_jsxs(Row, { gap: "xs", align: "center", children: [_jsx(Button, { variant: "ghost", size: "sm", icon: I.back, "aria-label": "Scroll backward", disabled: edges.atStart, onClick: () => page(-1) }), _jsx(Button, { variant: "ghost", size: "sm", icon: chevronRight, "aria-label": "Scroll forward", disabled: edges.atEnd, onClick: () => page(1) })] }))] }))] }), _jsx("div", { ref: regionRef, className: styles.rail, style: { '--card-rail-gap': `var(--space-${gap})` }, ...(ariaLabel !== undefined
                    ? { role: 'region', 'aria-label': ariaLabel, tabIndex: 0 }
                    : {}), children: children })] }));
}
//# sourceMappingURL=CardRail.js.map